import { FunctionComponent, useState, useEffect } from "react";
import { useAppSelector } from "./store/hooks";
import { getDB } from "./Firebase";
import {
  addDoc, collection, serverTimestamp,
  query, where, getDocs, orderBy, doc, getDoc,
} from "firebase/firestore";

type SupportPanelProps = { onClose: () => void };
type View = "main" | "form" | "ticket";

interface Ticket {
  id: string;
  subject: string;
  body: string;
  status: string;
  createdAt: any;
  adminReply?: string;
  userFollowUp?: string;
}

const STATUS_LABEL: Record<string, string> = {
  open:        "Открыт",
  in_progress: "В работе",
  resolved:    "Решён",
};

const SUBJECTS = [
  "Проблема с пополнением",
  "Проблема с выводом",
  "Технический баг",
  "Вопрос по игровой механике",
  "Пожелания и предложения",
  "Другое",
];

const FAQ = [
  ["Монеты не пришли после оплаты",
   "Зачисление происходит автоматически. Если прошло более 10 минут — создайте обращение с суммой и временем."],
  ["Соперник не отвечает в игре",
   "Если соперник не возобновил игру в течение 2 минут, нажмите «Сдаться» — победа засчитается вам."],
  ["Как пригласить друга",
   "Создайте онлайн-игру, скопируйте код комнаты и передайте другу. Код вводится в главном меню."],
  ["Как вывести средства",
   "Нажмите на баланс → вкладка «Вывести». Минимальная сумма 500 ₽, срок — до 24 часов."],
];

const SupportPanel: FunctionComponent<SupportPanelProps> = ({ onClose }) => {
  const profile = useAppSelector(s => s.user.profile);

  const [view, setView]         = useState<View>("main");
  const [tickets, setTickets]   = useState<Ticket[]>([]);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [contacts, setContacts] = useState({ telegram: "https://t.me/nardy_support", email: "support@nardy.io" });
  const [followUp, setFollowUp] = useState("");
  const [fuLoading, setFuLoading] = useState(false);
  const [fuSent, setFuSent]     = useState(false);

  const [subject, setSubject] = useState(SUBJECTS[0]);
  const [body, setBody]       = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [sent, setSent]       = useState(false);

  useEffect(() => {
    // Загружаем контакты и тикеты
    if (!profile) return;

    getDoc(doc(getDB(), "settings", "contacts"))
      .then(snap => { if (snap.exists()) setContacts(c => ({ ...c, ...snap.data() })); })
      .catch(() => {});

    getDocs(query(collection(getDB(), "support_tickets"), where("uid", "==", profile.uid)))
      .then(snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() } as Ticket));
        list.sort((a, b) => {
          const tA = a.createdAt?.toMillis?.() ?? 0;
          const tB = b.createdAt?.toMillis?.() ?? 0;
          return tB - tA;
        });
        setTickets(list);
      })
      .catch(() => {});
  }, [profile]);

  const fmtDate = (ts: any) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts._seconds * 1000);
    return d.toLocaleDateString("ru", { day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit" });
  };

  const handleFollowUp = async () => {
    if (!followUp.trim() || !selected || !profile) return;
    setFuLoading(true);
    try {
      const { updateDoc, doc: fsDoc } = await import("firebase/firestore");
      await updateDoc(fsDoc(getDB(), "support_tickets", selected.id), {
        userFollowUp: followUp.trim(),
        status: "open",
      });
      setFuSent(true);
      setFollowUp("");
      // Обновляем тикет в локальном состоянии
      const updated = { ...selected, userFollowUp: followUp.trim(), status: "open" };
      setSelected(updated);
      setTickets(ts => ts.map(t => t.id === selected.id ? updated : t));
    } catch (e: any) {
      alert("Ошибка: " + e.message);
    }
    setFuLoading(false);
  };

  const handleSubmit = async () => {
    if (!body.trim()) { setError("Опишите проблему"); return; }
    if (!profile)     { setError("Необходима авторизация"); return; }
    setLoading(true); setError("");
    try {
      await addDoc(collection(getDB(), "support_tickets"), {
        uid: profile.uid, nickname: profile.nickname,
        subject, body: body.trim(), status: "open",
        createdAt: serverTimestamp(),
      });
      setSent(true);
      setBody(""); setSubject(SUBJECTS[0]);
      // Обновляем список
      getDocs(query(collection(getDB(), "support_tickets"), where("uid", "==", profile.uid)))
        .then(snap => {
          const list = snap.docs.map(d => ({ id: d.id, ...d.data() } as Ticket));
          list.sort((a, b) => ((b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0)));
          setTickets(list);
        }).catch(() => {});
    } catch (e: any) { setError("Ошибка: " + e.message); }
    setLoading(false);
  };

  return (
    <div className="Overlay" onClick={onClose}>
      <div className="Modal Support-modal" onClick={e => e.stopPropagation()}>

        <div className="Modal-header">
          <div className="Modal-title">Поддержка</div>
          <button className="Modal-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Просмотр тикета ── */}
        {view === "ticket" && selected && (
          <div className="Support-content">
            <button className="Support-back-btn" onClick={() => { setView("main"); setSelected(null); setFuSent(false); setFollowUp(""); }}>
              ← Назад
            </button>
            <div className="Support-ticket-detail">
              <div className="Support-ticket-subject">{selected.subject}</div>
              <div className="Support-ticket-meta">
                {fmtDate(selected.createdAt)}
                <span className={`Support-status ${selected.status}`}>{STATUS_LABEL[selected.status] ?? selected.status}</span>
              </div>

              {/* Сообщение пользователя */}
              <div className="Support-ticket-msg user">
                <div className="Support-ticket-msg-label">Ваше сообщение</div>
                <div className="Support-ticket-msg-body">{selected.body}</div>
              </div>

              {/* Ответ поддержки */}
              {selected.adminReply && (
                <div className="Support-ticket-msg admin">
                  <div className="Support-ticket-msg-label">Ответ поддержки</div>
                  <div className="Support-ticket-msg-body">{selected.adminReply}</div>
                </div>
              )}

              {/* Уточнение пользователя (если было) */}
              {selected.userFollowUp && (
                <div className="Support-ticket-msg user">
                  <div className="Support-ticket-msg-label">Ваше уточнение</div>
                  <div className="Support-ticket-msg-body">{selected.userFollowUp}</div>
                </div>
              )}

              {/* Форма уточнения — только если admin уже ответил и follow-up ещё не отправлен */}
              {selected.adminReply && !selected.userFollowUp && !fuSent && (
                <div className="Support-followup">
                  <div className="Support-ticket-msg-label" style={{marginBottom:6}}>Уточнение или новый вопрос</div>
                  <textarea
                    className="Support-textarea"
                    rows={3}
                    placeholder="Если остался вопрос — напишите здесь…"
                    value={followUp}
                    onChange={e => setFollowUp(e.target.value)}
                    maxLength={1000}
                  />
                  <button
                    className="Support-submit"
                    style={{marginTop:8}}
                    onClick={handleFollowUp}
                    disabled={fuLoading || !followUp.trim()}
                  >
                    {fuLoading ? <span className="Btn-spinner" /> : "Отправить уточнение"}
                  </button>
                </div>
              )}

              {fuSent && (
                <div className="Support-sent-notice">Уточнение отправлено — мы ответим в течение 24 часов.</div>
              )}

              {!selected.adminReply && (
                <div className="Support-ticket-pending">Ожидает ответа. Обычно отвечаем в течение 24 часов.</div>
              )}
            </div>
          </div>
        )}

        {/* ── Главный экран ── */}
        {view === "main" && (
          <div className="Support-content">

            {/* Мои обращения */}
            {tickets.length > 0 && (
              <div className="Support-tickets-section">
                <div className="Support-section-title">Мои обращения</div>
                {tickets.map(t => (
                  <button key={t.id} className="Support-ticket-row" onClick={() => { setSelected(t); setView("ticket"); }}>
                    <div className="Support-ticket-row-left">
                      <div className="Support-ticket-row-subject">{t.subject}</div>
                      <div className="Support-ticket-row-date">{fmtDate(t.createdAt)}</div>
                    </div>
                    <span className={`Support-status ${t.status}`}>{STATUS_LABEL[t.status] ?? t.status}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Форма нового обращения */}
            <div className="Support-section-title" style={{marginTop: tickets.length ? 20 : 0}}>
              Новое обращение
            </div>

            {sent && (
              <div className="Support-sent-notice">Обращение отправлено. Ответ придёт в течение 24 часов.</div>
            )}

            <div className="Support-form">
              <select className="Support-select" value={subject} onChange={e => { setSubject(e.target.value); setSent(false); }}>
                {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <textarea
                className="Support-textarea"
                rows={4}
                placeholder="Опишите проблему подробно…"
                value={body}
                onChange={e => { setBody(e.target.value); setSent(false); }}
                maxLength={2000}
              />
              <div className="Support-chars">{body.length}/2000</div>
              {error && <div className="Support-error">{error}</div>}
              <button className="Support-submit" onClick={handleSubmit} disabled={loading || !body.trim()}>
                {loading ? <span className="Btn-spinner" /> : "Отправить"}
              </button>
            </div>

            {/* FAQ */}
            <div className="Support-section-title" style={{marginTop: 20}}>Частые вопросы</div>
            <div className="Support-faq">
              {FAQ.map(([q, a], i) => (
                <details key={i} className="Support-faq-item">
                  <summary className="Support-faq-q">{q}</summary>
                  <div className="Support-faq-a">{a}</div>
                </details>
              ))}
            </div>

            {/* Контакты — строка внизу */}
            <div className="Support-footer">
              <a href={contacts.telegram} target="_blank" rel="noopener noreferrer">{contacts.telegram.replace("https://","")}</a>
              <span>·</span>
              <a href={`mailto:${contacts.email}`}>{contacts.email}</a>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default SupportPanel;
